import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Observable } from 'rxjs';
import { finalize, mergeMap } from 'rxjs/operators';
import { DownloadService } from 'src/app/services/api/download.service';
import { UserActivityService } from 'src/app/services/user-activity.service';
import { ViewerTypeExtensions } from 'src/app/shared/components/document-preview/document-viewer-extensions';
import { DocumentPreviewService } from 'src/app/shared/components/document-preview/services/document-preview.service';
import { SlideDTO } from '@app/model/slideDTO.model';
import { BundleType, EntityTypeId, FieldName, LibraryItem, MediaPresignApiService } from 'library-explorer';

@Component({
    selector: 'app-slide-documents',
    templateUrl: './slide-documents.component.html',
    styleUrls: ['./slide-documents.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SlideDocumentsComponent implements OnInit {
  @Input() public slideData: SlideDTO;
  @Input() public animationEnabled = true;
  @Input() public documentDownload = true;

  public loadingDocumentId: string;
  public documents: Array<LibraryItem & { previewAvailable?: boolean }> = [];

  constructor(
    private readonly userActivityService: UserActivityService,
    private readonly downloadService: DownloadService,
    private readonly mediaPresignApiService: MediaPresignApiService,
    private readonly documentPreviewService: DocumentPreviewService
  ) { }

  ngOnInit() {
    const availablePreviewExtensions = [].concat(...Object.values(ViewerTypeExtensions));
    this.documents = this.slideData.documents.map(document => ({
      ...document,
      previewAvailable: availablePreviewExtensions.indexOf(document.file.extension.toLocaleLowerCase()) !== -1
    }));
  }

  public preview(document: LibraryItem & { previewAvailable?: boolean }): void {
    if (!document.previewAvailable) {
      return;
    }

    this.getPreSignedUrl(document).subscribe(presignedUrl => {
      this.documentPreviewService.openPreviewDialog({ file: document.file, presignedUrl, entityId: document.id, isFileDownloadEnabled: document.allowDownload });
      this.userActivityService.logUserPreviewFileActivity(document.id);
    });
  }

  public download(document: LibraryItem): void {
    this.loadingDocumentId = document.id;

    this.getPreSignedUrl(document)
      .pipe(
        mergeMap((url: string) => this.downloadService.downloadFile(url, document.file.provider, document.file.filename)),
        finalize(() => this.loadingDocumentId = null)
      )
      .subscribe(() => {
        this.userActivityService.logUserDownloadFileActivity(document.id);
      });
  }

  private getPreSignedUrl(document: LibraryItem): Observable<any> {
    return this.mediaPresignApiService.getPreSignedUrl(EntityTypeId.NODE, BundleType.LIBRARY, FieldName.AWS_FILE, document.file);
  }
}
